import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { StickyFooter } from '../components/StickyFooter';
import { ClockIcon, UserIcon, ArrowRightIcon } from 'lucide-react';
type Category = 'All' | 'Staffing Tips' | 'Industry News' | 'Caregiver Stories' | 'Clinical Insights';
const CATEGORIES: Category[] = ['All', 'Staffing Tips', 'Industry News', 'Caregiver Stories', 'Clinical Insights'];
const BLOG_POSTS = [{
  id: '1',
  title: 'How to Reduce Agency Costs by 40% with Direct Staffing',
  excerpt: 'Discover the hidden fees in traditional agency models and learn how direct staffing platforms are helping facilities save thousands while paying workers more.',
  category: 'Staffing Tips',
  readTime: '5 min read',
  date: 'Oct 12, 2023',
  author: {
    name: 'Ese Uko',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop',
  featured: true
}, {
  id: '2',
  title: "Ontario's New Healthcare Staffing Regulations: What You Need to Know",
  excerpt: 'A comprehensive breakdown of the latest legislative changes affecting long-term care staffing ratios and compliance requirements.',
  category: 'Industry News',
  readTime: '7 min read',
  date: 'Oct 05, 2023',
  author: {
    name: 'Sarah Jenkins, RN',
    avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800&auto=format&fit=crop'
}, {
  id: '3',
  title: 'A Day in the Life of a Care Nomad PSW',
  excerpt: 'Follow Maria as she navigates her flexible schedule, chooses her preferred shifts, and delivers compassionate care on her own terms.',
  category: 'Caregiver Stories',
  readTime: '4 min read',
  date: 'Sep 28, 2023',
  author: {
    name: 'Maria Gonzalez',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=800&auto=format&fit=crop'
}, {
  id: '4',
  title: 'Best Practices for Managing Overnight Shifts in Long-Term Care',
  excerpt: 'Clinical strategies to ensure patient safety, staff alertness, and smooth handoffs during the critical night hours.',
  category: 'Clinical Insights',
  readTime: '6 min read',
  date: 'Sep 20, 2023',
  author: {
    name: 'David Chen, RN',
    avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1584515933487-779824d29309?w=800&auto=format&fit=crop'
}, {
  id: '5',
  title: 'Why Nurse-Led Vetting Produces Better Patient Outcomes',
  excerpt: "When clinical professionals screen candidates instead of HR generalists, the quality of care improves dramatically. Here's the data.",
  category: 'Clinical Insights',
  readTime: '8 min read',
  date: 'Sep 15, 2023',
  author: {
    name: 'Ese Uko',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&auto=format&fit=crop'
}, {
  id: '6',
  title: 'The Future of Healthcare Staffing in Canada: 2025 Trends',
  excerpt: 'From AI-driven matching to the rise of the gig economy in nursing, explore what the next few years hold for healthcare HR.',
  category: 'Industry News',
  readTime: '5 min read',
  date: 'Sep 02, 2023',
  author: {
    name: 'Michael Ross',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=800&auto=format&fit=crop'
}, {
  id: '7',
  title: 'From Burnout to Balance: How Flexible Scheduling Changed My Career',
  excerpt: 'An honest account of leaving full-time hospital staff to embrace the freedom of per-diem shifts, and rediscovering the joy of nursing.',
  category: 'Caregiver Stories',
  readTime: '4 min read',
  date: 'Aug 25, 2023',
  author: {
    name: 'Jessica Wong, RPN',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&auto=format&fit=crop'
  },
  imageUrl: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&auto=format&fit=crop'
}];
export function Blog() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const filteredPosts = BLOG_POSTS.filter(post => activeCategory === 'All' || post.category === activeCategory);
  const featuredPost = activeCategory === 'All' ? BLOG_POSTS.find(p => p.featured) : filteredPosts[0];
  const gridPosts = activeCategory === 'All' ? filteredPosts.filter(p => !p.featured) : filteredPosts.slice(1);
  return <div className="min-h-screen w-full bg-gray-50 flex flex-col pt-16" data-id="element-899">
      <Navbar data-id="element-900" />

      <main className="flex-1" data-id="element-901">
        {/* Hero Section */}
        <section className="w-full bg-gradient-to-b from-soft-teal to-gray-50 py-16 lg:py-24 border-b border-gray-100" data-id="element-902">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-903">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-text-dark tracking-tight mb-6" data-id="element-904">
              Care Nomad <span className="text-medical-blue" data-id="element-905">Blog</span>
            </h1>
            <p className="text-lg sm:text-xl text-text-muted max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-906">
              Insights, updates, and stories from the frontlines of healthcare
              staffing in Canada.
            </p>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3" data-id="element-907">
              {CATEGORIES.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeCategory === category ? 'bg-medical-blue text-white shadow-md' : 'bg-white text-text-muted hover:bg-gray-100 border border-gray-200'}`} data-id="element-908">
                  {category}
                </button>)}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-909">
          {/* Featured Post */}
          {featuredPost && <div className="mb-16 bg-white rounded-3xl shadow-float overflow-hidden group cursor-pointer border border-gray-100 transition-all hover:shadow-float-hover" data-id="element-910">
              <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-911">
                <div className="relative h-64 lg:h-auto overflow-hidden" data-id="element-912">
                  <img src={featuredPost.imageUrl} alt={featuredPost.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-913" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-medical-blue" data-id="element-914">
                    {featuredPost.category}
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center" data-id="element-915">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-text-dark mb-4 leading-tight group-hover:text-medical-blue transition-colors" data-id="element-916">
                    {featuredPost.title}
                  </h2>
                  <p className="text-lg text-text-muted mb-8 leading-relaxed" data-id="element-917">
                    {featuredPost.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-100" data-id="element-918">
                    <div className="flex items-center gap-3" data-id="element-919">
                      <img src={featuredPost.author.avatar} alt={featuredPost.author.name} className="w-10 h-10 rounded-full object-cover" data-id="element-920" />
                      <div data-id="element-921">
                        <div className="text-sm font-bold text-text-dark" data-id="element-922">
                          {featuredPost.author.name}
                        </div>
                        <div className="text-xs text-text-muted flex items-center gap-2" data-id="element-923">
                          <span data-id="element-924">{featuredPost.date}</span>
                          <span data-id="element-925">•</span>
                          <span className="flex items-center gap-1" data-id="element-926">
                            <ClockIcon className="w-3 h-3" data-id="element-927" />{' '}
                            {featuredPost.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-soft-teal flex items-center justify-center text-medical-blue group-hover:bg-medical-blue group-hover:text-white transition-colors" data-id="element-928">
                      <ArrowRightIcon className="w-5 h-5" data-id="element-929" />
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* Blog Grid */}
          {gridPosts.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-930">
              {gridPosts.map(post => <div key={post.id} className="bg-white rounded-2xl shadow-card overflow-hidden group cursor-pointer border border-gray-100 hover:shadow-float transition-all flex flex-col" data-id="element-931">
                  <div className="relative h-52 overflow-hidden" data-id="element-932">
                    <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-933" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-medical-blue" data-id="element-934">
                      {post.category}
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-1" data-id="element-935">
                    <h3 className="text-xl font-bold text-text-dark mb-3 leading-tight group-hover:text-medical-blue transition-colors line-clamp-2" data-id="element-936">
                      {post.title}
                    </h3>
                    <p className="text-sm text-text-muted mb-6 leading-relaxed line-clamp-2 flex-1" data-id="element-937">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center gap-3 pt-5 border-t border-gray-100 mt-auto" data-id="element-938">
                      <img src={post.author.avatar} alt={post.author.name} className="w-8 h-8 rounded-full object-cover" data-id="element-939" />
                      <div data-id="element-940">
                        <div className="text-xs font-bold text-text-dark" data-id="element-941">
                          {post.author.name}
                        </div>
                        <div className="text-[11px] text-text-muted flex items-center gap-1.5 mt-0.5" data-id="element-942">
                          <span data-id="element-943">{post.date}</span>
                          <span data-id="element-944">•</span>
                          <span className="flex items-center gap-1" data-id="element-945">
                            <ClockIcon className="w-3 h-3" data-id="element-946" /> {post.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-20" data-id="element-947">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-948">
                <UserIcon className="w-8 h-8 text-gray-400" data-id="element-949" />
              </div>
              <h3 className="text-xl font-bold text-text-dark mb-2" data-id="element-950">
                No posts found
              </h3>
              <p className="text-text-muted" data-id="element-951">
                There are currently no posts in the "{activeCategory}" category.
              </p>
            </div>}
        </div>
      </main>
      <StickyFooter data-id="element-952" />
    </div>;
}